import { Box, Link, Table, HStack, Text, VStack, Span } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import Informative from "@hydrologiq/shared/Informative"
import { useNavigate } from "react-router-dom"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { useMemo, useState } from "react"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"

type DeploymentFilter = "all" | "current" | "historical"

interface DeploymentsTableProps {
  deployments: gqlTypes.Deployment[]
  loading?: boolean
  asset?: string
}

function DeploymentsTable({ deployments, loading, asset }: DeploymentsTableProps) {
  const route = useRoute()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<DeploymentFilter>("all")

  // Filter deployments based on selected filter
  const filteredDeployments = useMemo(() => {
    if (!deployments) return []

    switch (filter) {
      case "current":
        return deployments.filter((deployment) => {
          return deployment.active
        })
      case "historical":
        return deployments.filter((deployment) => {
          return !deployment.active
        })
      case "all":
      default:
        return deployments
    }
  }, [deployments, filter])

  // All possible column definitions based on DeploymentAttributeKind enum
  const allAttributeColumns = [
    { key: gqlTypes.DeploymentAttributeKind.TotalCo2e, label: "Total CO2e (kg)" },
    { key: gqlTypes.DeploymentAttributeKind.TotalEnergy, label: "Total Energy (kWh)" },
    { key: gqlTypes.DeploymentAttributeKind.TotalH2, label: "Total Hydrogen (kg)" },
    { key: gqlTypes.DeploymentAttributeKind.Co2eSaved, label: "CO2e Saved (kg)" },
    { key: gqlTypes.DeploymentAttributeKind.Co2eKWh, label: "CO2e (kg/kWh)" },
    { key: gqlTypes.DeploymentAttributeKind.Co2eKg, label: "CO2e (kg/h2)" },
  ]

  // Filter to only show columns that have data in at least one deployment
  const attributeColumns = useMemo(() => {
    if (!filteredDeployments || filteredDeployments.length === 0) return []

    const columnsWithData = allAttributeColumns.filter((column) => {
      return filteredDeployments.some((deployment) => {
        const attribute = deployment.attributes?.find((attr) => attr.kind === column.key)
        return attribute && attribute.value
      })
    })

    return columnsWithData
  }, [filteredDeployments])

  // Helper to get attribute value for a deployment by attribute kind
  const getAttributeValue = (deployment: gqlTypes.Deployment, attributeKind: gqlTypes.DeploymentAttributeKind) => {
    const attribute = deployment.attributes?.find((attr) => attr.kind === attributeKind)
    return attribute ? attribute.value : null
  }

  const totalColumns = 3 + attributeColumns.length // Name + Start + End columns + attribute columns

  // Helper to format date strings
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-"
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return "-"
    }
  }

  const disclamer = asset !== undefined

  return (
    <VStack align="stretch" gap={4}>
      {/* Segment Control for filtering */}
      <SegmentedControl
        variant="hydrologiq"
        value={filter}
        onValueChange={(details) => setFilter(details.value as DeploymentFilter)}
        items={[
          { value: "all", label: "All" },
          { value: "current", label: "Current" },
          { value: "historical", label: "Historical" },
        ]}
      />

      {filteredDeployments.length > 0 || loading ? (
        <Box overflowX="auto">
          {/* @ts-ignore */}
          <Table.Root variant="hydrologiq">
            {disclamer && <Table.Caption>*total for the deployment, not per asset</Table.Caption>}
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  <HStack>
                    <Text>Name</Text>
                  </HStack>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <HStack>
                    <Text>Start Date</Text>
                  </HStack>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <HStack>
                    <Text>End Date</Text>
                  </HStack>
                </Table.ColumnHeader>
                {attributeColumns.map((column) => (
                  <Table.ColumnHeader key={column.key}>
                    <HStack>
                      <Text>
                        {column.label}
                        {disclamer && "*"}
                      </Text>
                    </HStack>
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {loading
                ? [...Array(10)].map((_, index) => (
                    <Table.Row height="40px" key={index}>
                      <Table.Cell colSpan={totalColumns}></Table.Cell>
                    </Table.Row>
                  ))
                : filteredDeployments.map((deployment) => {
                    const deploymentPath = `${route.endsWith("/") ? route : route + "/"}${deployment.id}`
                    const isAccessible = !deployment.data_only
                    const displayName =
                      deployment.name.length > 35 ? `${deployment.name.slice(0, 35)}...` : deployment.name

                    return (
                      <Table.Row key={deployment.id}>
                        <Table.Cell width="300px">
                          {isAccessible ? (
                            <Tooltip content={`Explore ${deployment.name}`}>
                              {/* @ts-ignore */}
                              <Link
                                variant="primary"
                                href={deploymentPath}
                                onClick={(e) => {
                                  e.preventDefault()
                                  navigate(deploymentPath)
                                }}
                              >
                                {displayName}
                              </Link>
                            </Tooltip>
                          ) : (
                            <Tooltip content="Data-only deployment - no manager access">
                              <Text color="fg.muted">{displayName}</Text>
                            </Tooltip>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">{formatDate(deployment.start)}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm" color={deployment.end ? "inherit" : "gray.400"}>
                            {formatDate(deployment.end)}
                          </Text>
                        </Table.Cell>
                        {attributeColumns.map((column) => {
                          const attributeValue = getAttributeValue(deployment, column.key)
                          return (
                            <Table.Cell key={column.key}>
                              {attributeValue ? (
                                <Text fontSize="sm">{attributeValue}</Text>
                              ) : (
                                <Text color="fg.subtle" fontSize="sm">
                                  -
                                </Text>
                              )}
                            </Table.Cell>
                          )
                        })}
                      </Table.Row>
                    )
                  })}
            </Table.Body>
          </Table.Root>
        </Box>
      ) : (
        <Informative
          heading="No deployments"
          text={filter === "all" ? "No deployments found" : `No ${filter} deployments found`}
        />
      )}
    </VStack>
  )
}

export default DeploymentsTable
